import { SelectMenu } from "melodies-source/SelectMenu";
import { SvgCheck } from "melodies-source/Svgs/Check";
import { SvgDropdown } from "melodies-source/Svgs/Dropdown";
import {
  Body1,
  Body2,
  Caption,
  Label,
  Selected,
  Subtitle2,
} from "melodies-source/Text";
import { Fragment, useState } from "react";
import styled, { css } from "styled-components";

export type SelectOption = {
  label: React.ReactNode;
  caption?: React.ReactNode;
  value?: string;
  options?: Omit<SelectOption, "options">[];
  disabled?: boolean;
};

interface SelectProps {
  label?: string;
  onChange?: (v: string) => void;
  onArrayChange?: (v: string[]) => void;
  value?: string;
  valueArray?: string[];
  options: SelectOption[];
  placeholder?: string;
  style?: React.CSSProperties;
  helperText?: string;
  hasError?: boolean;
}

export const Select = ({
  label,
  onChange,
  onArrayChange,
  value,
  options,
  placeholder,
  helperText,
  valueArray,
  hasError,
  ...props
}: SelectProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const selected: SelectOption = options
    ?.flatMap((curr) => curr.options || [curr])
    ?.find((option) => option.value === value);

  const selectedArray: SelectOption[] = options
    ?.flatMap((curr) => curr.options || [curr])
    .filter((option) => valueArray?.includes(option.value));

  return (
    <SelectWrapper {...props}>
      {label && <Label>{label}</Label>}
      <SelectedValue
        onClick={() => {
          setIsMenuOpen(true);
        }}
        isOpen={isMenuOpen}
      >
        <TextContainer>
          {!!selectedArray?.length ? (
            <Selected style={{ fontWeight: 500 }}>
              {selectedArray?.map(({ label }) => label)?.join(", ")}
            </Selected>
          ) : selected ? (
            <Selected style={{ fontWeight: 500 }}>{selected?.label}</Selected>
          ) : (
            <Body2>{placeholder}</Body2>
          )}
        </TextContainer>
        <Dropdown />
      </SelectedValue>
      <SelectMenu isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)}>
        {options.map((option, idx) => {
          return !option.options ? (
            <ListItemComponent
              label={option.label}
              disabled={option.disabled}
              caption={option.caption}
              key={`select-option-${idx}`}
              selected={
                valueArray !== undefined
                  ? valueArray?.includes(option.value)
                  : selected?.value === option.value
              }
              onClick={() => {
                if (option.disabled) {
                  return;
                }
                setIsMenuOpen(false);
                if (valueArray !== undefined) {
                  let update = [...valueArray, option.value];
                  if (valueArray?.includes(option.value)) {
                    update = update.filter((o) => o !== option.value);
                  }
                  onArrayChange?.(update);
                } else {
                  onChange?.(option.value);
                }
              }}
            />
          ) : (
            <Fragment key={`select-option-${idx}`}>
              <SelectListSubtitle>
                <Subtitle2>{option.label}</Subtitle2>
              </SelectListSubtitle>
              {option.options.map((o, nIdx) => (
                <ListItemComponent
                  {...o}
                  key={`nested-select-option-${idx}-${nIdx}`}
                  selected={selected?.value === o.value}
                  onClick={() => {
                    setIsMenuOpen(false);
                    onChange(o.value);
                  }}
                />
              ))}
            </Fragment>
          );
        })}
      </SelectMenu>
      {helperText && (
        <Caption
          style={{ marginTop: 4, color: hasError ? "#FF0000" : "#ffffff" }}
        >
          {helperText}
        </Caption>
      )}
    </SelectWrapper>
  );
};

type ListItemComponentProps = Pick<
  SelectOption,
  "label" | "caption" | "disabled"
> & {
  selected?: boolean;
  onClick?: VoidFunction;
};

const ListItemComponent = ({
  selected,
  label,
  caption,
  disabled,
  ...props
}: ListItemComponentProps) => {
  return (
    <SelectListItem {...props} $disabled={disabled}>
      <ListItemRow>
        {selected && (
          <CheckIconWrapper isSelected={selected}>
            <SvgCheck />
          </CheckIconWrapper>
        )}
        <Body1>{label}</Body1>
      </ListItemRow>
      {caption && <Caption>{caption}</Caption>}
    </SelectListItem>
  );
};

const TextContainer = styled.span`
  flex-grow: 1;
  overflow: hidden;
  ${Selected} {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
`;
type SelectedValueProps = {
  hasError?: boolean;
  isOpen?: boolean;
};
const SelectedValue = styled.div<SelectedValueProps>`
  display: flex;
  width: 100%;
  height: 44px;
  background: var(--component-background-color);
  border-radius: 6px;
  border: solid 1px var(--input-border-color);
  padding: 11px 12px;
  user-select: none;
  cursor: pointer;
  align-items: center;
  ${Body2} {
    color: var(--disabled-text-color);
  }
  ${(p) =>
    p.hasError &&
    css`
      border: solid 1px ${(p) => p.theme.colors.error};
    `};
  ${(p) =>
    p.isOpen &&
    css`
      box-shadow: 0 0 8px 0 rgba(0, 0, 0, 0.24);
    `};
`;

const ListItemRow = styled.div`
  display: flex;
  align-items: center;
  flex-grow: 1;
  ${Body1} {
    color: var(--text-color);
    line-height: 20px;
    font-weight: 500;
  }
`;

const ListItem = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 44px;
  padding: 8px 12px 8px 44px;
  position: relative;
  min-width: 180px;
  user-select: none;
`;

const SelectListItem = styled(ListItem)<{ $disabled?: boolean }>`
  :hover {
    cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
    background: var(--hover-background-color);
  }
  & + & {
    border-top: solid 1px var(--box-shadow-color);
  }
  ${({ $disabled }) =>
    $disabled &&
    css`
      ${Body1} {
        color: var(--disabled-text-color);
      }
      path {
        color: var(--disabled-text-color);
      }
    `};
`;

const SelectListSubtitle = styled(ListItem)`
  justify-content: flex-end;
  padding-left: 12px;
  ${Subtitle2} {
    color: var(--secondary-text-color);
  }
  ${SelectListItem} + & {
    border-top: solid 1px var(--box-shadow-color);
  }
  &:first-child {
    min-height: 36px;
  }
`;

type CheckIconProps = {
  isSelected: boolean;
};
const CheckIconWrapper = styled.div<CheckIconProps>`
  display: flex;
  width: 20px;
  height: 20px;
  position: absolute;
  left: 12px;
  ${(p) =>
    p.isSelected
      ? css`
          color: var(--main-color);
        `
      : css`
          opacity: 0;
        `}
`;

const SelectWrapper = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  ${Label} {
    margin-bottom: 4px;
  }
`;

const Dropdown = styled(SvgDropdown)`
  color: var(--secondary-text-color);
`;
